import type { Edulead } from 'edulead';
import type { ReviewChildRecord, ReviewRecord } from '../../custom-components/ExportReviewGrid';
import { enumLabel } from '../../services/edulead-v4';

/**
 * The export wizard reads the Go API's exports (`/v4` ExportService) through these shapes, so the
 * steps and the shared grids keep the names and values they compared before: states, entities,
 * targets and steps as their stored names (`REVIEW`, `CONTACT`, `HUBSPOT`, `FETCHING_DATA`),
 * options under legacy's metadata keys, review records as the grid's rows.
 */

/** The export as the wizard steps read it: the API's export with its enums as stored names. */
export type ExportView = Omit<Edulead.EduleadV4Export, 'state' | 'entity' | 'target' | 'step'> & {
  /** The state's stored name ('' when unset). */
  status: string;
  entity: string;
  target: string;
  step: string;
};

export function exportViewOf(e: Edulead.EduleadV4Export): ExportView {
  const { state, entity, target, step, ...rest } = e;
  return {
    ...rest,
    status: enumLabel(state, 'EXPORT_STATE_'),
    entity: enumLabel(entity, 'EXPORT_ENTITY_'),
    target: enumLabel(target, 'EXPORT_TARGET_'),
    step: enumLabel(step, 'EXPORT_STEP_'),
  };
}

/** A stored entity or target name as the API's enum value. */
export const EXPORT_ENTITIES: Record<string, Edulead.EduleadV4ExportEntity> = {
  CONTACT: 'EXPORT_ENTITY_CONTACT',
  INSTITUTION: 'EXPORT_ENTITY_INSTITUTION',
};

export const EXPORT_TARGETS: Record<string, Edulead.EduleadV4ExportTarget> = {
  CSV: 'EXPORT_TARGET_CSV',
  JSON: 'EXPORT_TARGET_JSON',
  HUBSPOT: 'EXPORT_TARGET_HUBSPOT',
  SALESFORCE: 'EXPORT_TARGET_SALESFORCE',
};

/** The option values the forms hold, by legacy's key, as the API's enum values. */
const EMAIL_PROVIDERS: Record<string, Edulead.EduleadV4ExportEmailProvider> = {
  NONE: 'EXPORT_EMAIL_PROVIDER_NONE',
  ZEROBOUNCE: 'EXPORT_EMAIL_PROVIDER_ZEROBOUNCE',
  NEVERBOUNCE: 'EXPORT_EMAIL_PROVIDER_NEVERBOUNCE',
};

const INVALID_EMAIL_HANDLINGS: Record<string, Edulead.EduleadV4ExportInvalidEmailHandling> = {
  SKIP: 'EXPORT_INVALID_EMAIL_HANDLING_SKIP',
  INCLUDE: 'EXPORT_INVALID_EMAIL_HANDLING_INCLUDE',
};

const COMPANYLESS_CONTACTS: Record<string, Edulead.EduleadV4ExportCompanylessContacts> = {
  IMPORT_ANYWAY: 'EXPORT_COMPANYLESS_CONTACTS_IMPORT_ANYWAY',
  SKIP: 'EXPORT_COMPANYLESS_CONTACTS_SKIP',
  CREATE_COMPANY_AND_IMPORT: 'EXPORT_COMPANYLESS_CONTACTS_CREATE_COMPANY_AND_IMPORT',
};

const ACCOUNT_ASSOCIATIONS: Record<string, Edulead.EduleadV4ExportAccountAssociation> = {
  MULTIPLE: 'EXPORT_ACCOUNT_ASSOCIATION_MULTIPLE',
  SINGLE: 'EXPORT_ACCOUNT_ASSOCIATION_SINGLE',
};

const SALESFORCE_OBJECTS: Record<string, Edulead.EduleadV4ExportSalesforceObject> = {
  Contact: 'EXPORT_SALESFORCE_OBJECT_CONTACT',
  Lead: 'EXPORT_SALESFORCE_OBJECT_LEAD',
};

/** The key a table maps to `value`, or undefined for an unset or unknown value. */
function keyOf<V extends string>(
  table: Record<string, V>,
  value: V | undefined,
): string | undefined {
  return Object.keys(table).find((k) => table[k] === value);
}

/**
 * The options as the option forms hold them: legacy's `metadata.options` keys and values
 * (`revalidateEmailsProvider: 'ZEROBOUNCE'`, `salesforceObject: 'Lead'`). Unset options are absent,
 * so the forms show their defaults.
 */
export function optionKeysOf(
  o: Edulead.EduleadV4ExportOptions | undefined,
): Record<string, string | boolean> {
  const keys: Record<string, string | boolean | undefined> = {
    sendNotification: o?.send_notification,
    revalidateEmailsProvider: keyOf(EMAIL_PROVIDERS, o?.email_provider),
    invalidEmailHandling: keyOf(INVALID_EMAIL_HANDLINGS, o?.invalid_email_handling),
    contactsWithoutCompaniesBehavior: keyOf(COMPANYLESS_CONTACTS, o?.companyless_contacts),
    createHubspotList: o?.create_hubspot_list,
    hubspotListName: o?.hubspot_list_name || undefined,
    accountAssociationMode: keyOf(ACCOUNT_ASSOCIATIONS, o?.account_association),
    salesforceObject: keyOf(SALESFORCE_OBJECTS, o?.salesforce_object),
    includeChildInstitutions: o?.include_child_institutions,
    preserveParentChildRelationship: o?.preserve_hierarchy,
  };
  const out: Record<string, string | boolean> = {};
  for (const [k, v] of Object.entries(keys)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

/**
 * The forms' options as the API takes them. The API writes legacy's keys for the export's entity
 * and target, as the forms built them; `accountStrategy` has no field, the API always writes SKIP.
 */
export function optionsOf(keys: Record<string, unknown>): Edulead.EduleadV4ExportOptions {
  const text = (k: string) => (typeof keys[k] === 'string' ? (keys[k] as string) : '');
  return {
    send_notification: keys.sendNotification === true,
    email_provider: EMAIL_PROVIDERS[text('revalidateEmailsProvider')],
    invalid_email_handling: INVALID_EMAIL_HANDLINGS[text('invalidEmailHandling')],
    companyless_contacts: COMPANYLESS_CONTACTS[text('contactsWithoutCompaniesBehavior')],
    create_hubspot_list: keys.createHubspotList === true,
    hubspot_list_name: text('hubspotListName') || undefined,
    account_association: ACCOUNT_ASSOCIATIONS[text('accountAssociationMode')],
    salesforce_object: SALESFORCE_OBJECTS[text('salesforceObject')],
    include_child_institutions: keys.includeChildInstitutions === true,
    preserve_hierarchy: keys.preserveParentChildRelationship === true,
  };
}

/**
 * A review record's JSON object as the grid reads it. The API serves every value as text, other
 * JSON values as their compact JSON: booleans, arrays and objects are parsed back (the grid tests
 * `isPrimary`, the slideout reads `changedFields` and `crmMatches`); numbers stay text, which the
 * cells render the same.
 */
function jsonValues(m: Record<string, string> | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(m ?? {})) {
    out[k] = parsedValue(v);
  }
  return out;
}

function parsedValue(v: string): unknown {
  if (v === 'true') return true;
  if (v === 'false') return false;
  if (v.startsWith('[') || v.startsWith('{')) {
    try {
      return JSON.parse(v);
    } catch {
      return v; // text that only looks like JSON
    }
  }
  return v;
}

/** The fields a review record and its children share, as the grids name them. */
function recordFields(
  r: Edulead.EduleadV4ExportReviewRecord | Edulead.EduleadV4ExportReviewRecordChild,
) {
  const code = r.decision_reason?.code;
  return {
    id_source: r.id ?? '',
    record_type: enumLabel(r.record_type, 'EXPORT_RECORD_TYPE_'),
    data: jsonValues(r.data),
    data_target: r.target_data ? jsonValues(r.target_data) : undefined,
    user_decision: enumLabel(r.decision, 'EXPORT_DECISION_') || null,
    operation: enumLabel(r.operation, 'EXPORT_OPERATION_') || undefined,
    id_crm: r.crm_id || undefined,
    export_status: enumLabel(r.export_status, 'EXPORT_RECORD_STATUS_') || undefined,
    export_error: r.export_error || undefined,
    decision_reason: code ? { code, context: jsonValues(r.decision_reason?.context) } : undefined,
  };
}

/** A review record as a row of the review and results grids. */
export type ReviewRow = ReviewRecord & {
  export_status?: string;
  export_error?: string;
  child_records?: ReviewChildRow[];
};

type ReviewChildRow = ReviewChildRecord & { export_status?: string; export_error?: string };

export function reviewRowOf(r: Edulead.EduleadV4ExportReviewRecord): ReviewRow {
  return {
    ...recordFields(r),
    system_data: r.system_data ? jsonValues(r.system_data) : undefined,
    enrichment: r.enrichment_type ? { type: r.enrichment_type } : undefined,
    email_status: r.email_status || undefined,
    child_records: r.children?.map(recordFields),
  };
}

/** The API's decision for a grid's (`TAKE`, `SKIP`). */
export function decisionOf(decision: string): Edulead.EduleadV4ExportDecision {
  return decision === 'TAKE' ? 'EXPORT_DECISION_TAKE' : 'EXPORT_DECISION_SKIP';
}

/** The API's record status for the results' stat filter (`SUCCESS`, `FAILED`, `SKIPPED`). */
export const RECORD_STATUSES: Record<string, Edulead.EduleadV4ExportRecordStatus> = {
  SUCCESS: 'EXPORT_RECORD_STATUS_SUCCESS',
  FAILED: 'EXPORT_RECORD_STATUS_FAILED',
  SKIPPED: 'EXPORT_RECORD_STATUS_SKIPPED',
};
